<script lang="ts">
  // One finding from GET /api/hr/insights, rendered. Dispatches on
  // render.chart_type to the SVG primitives in the BUI kit — the card knows
  // nothing about what the finding means, which is why adding a builder
  // server-side needs no change here.
  //
  // Deliberately NOT an extension of ui/InsightCard.svelte: that one is the
  // small number tile in an overview strip. Same word, different object (the
  // name collision comes from the ported edge-server card contract).
  import type { HRInsightCard } from '$lib/types';
  import Sparkline from '$lib/components/ui/Sparkline.svelte';
  import ColumnChart from '$lib/components/ui/ColumnChart.svelte';
  import Histogram from '$lib/components/ui/Histogram.svelte';
  import TimeBand from '$lib/components/ui/TimeBand.svelte';

  const { card }: { card: HRInsightCard } = $props();

  const series = $derived(card.graph?.series?.[0]);
  const labels = $derived(card.graph?.labels ?? []);
  const data = $derived(series?.data ?? []);
  const hasChart = $derived(card.render.chart_type !== 'none' && data.length > 0);
  const accent = $derived(card.render.accent);

  // Metrics worth putting on the face of the card. The payload carries more
  // than a card should show (ids, gates, raw counts behind a percentage), so
  // this is an explicit allow-list with display labels rather than a dump of
  // every key.
  const METRIC_LABELS: Record<string, string> = {
    typical_time: 'Typical',
    stddev_minutes: 'Spread',
    consistency_score: 'Consistency',
    samples: 'Days',
    hours: 'Hours',
    total_hours: 'Hours',
    late_rate_pc: 'Late rate',
    early_rate_pc: 'Early rate',
    forgot_rate_pc: 'Auto-closed',
    remote_share_pc: 'Remote',
    modal_share_pc: 'Share',
    completeness_rate_pc: 'Attended',
    drift_minutes_per_week: 'Drift',
    share_pc: 'Share',
    total_leave_days: 'Leave days',
    fullest_avg_headcount: 'Avg in',
    // HR flags (internal/analytics/flag_builders.go). Same allow-list rule:
    // a flag's metrics carry the gates and raw counts behind its copy, and
    // only the few worth a tile on the card's face are named here.
    //
    // Two keys on the same card should not share a label (the list renders
    // keyed by label). shownMetrics drops a collision rather than letting it
    // become a runtime each_key_duplicate, but the labels here are still
    // chosen to be distinct per card.
    office_days: 'Office days',
    median_arrival: 'Median in',
    median_departure: 'Median out',
    streak: 'Streak',
    drift_min_per_week: 'Drift',
    unexplained: 'Unexplained',
    quota: 'Quota',
    availed: 'Used',
    pace_ratio: 'Pace',
    adjacency_pc: 'Adjacent',
    leave_days: 'Leave days',
    window_days: 'Leave days',
    late_count: 'Late filed',
    early_count: 'Early filed',
    unfixed: 'Uncorrected'
    // Deliberately no change_vs_previous_pc: the summary sentence already
    // states the comparison in words ("up 8% from the previous month", or
    // "no comparable month before this one"), and a bare "Change 0%" tile
    // beside that reads as a measured zero when it often means no baseline.
  };

  function formatMetric(key: string, value: unknown): string {
    if (typeof value !== 'number') return String(value);
    if (key === 'consistency_score') return `${Math.round(value * 100)}%`;
    if (key.endsWith('_pc')) return `${value}%`;
    if (key === 'stddev_minutes') return `±${Math.round(value)} min`;
    if (key === 'drift_minutes_per_week' || key === 'drift_min_per_week')
      return `${value > 0 ? '+' : ''}${value} min/wk`;
    if (key === 'streak') return `${value} day${value === 1 ? '' : 's'}`;
    if (key === 'pace_ratio') return `${value}x`;
    if (key === 'hours' || key === 'total_hours') return `${value}h`;
    return String(value);
  }

  const shownMetrics = $derived.by(() => {
    const seen = new Set<string>();
    return Object.entries(card.metrics ?? {})
      .filter(([key, value]) => key in METRIC_LABELS && value != null && typeof value !== 'object')
      .map(([key, value]) => ({ label: METRIC_LABELS[key], value: formatMetric(key, value) }))
      .filter((m) => !seen.has(m.label) && seen.add(m.label))
      .slice(0, 4);
  });
</script>

<article class="tym-insight-finding tym-insight-finding--{card.severity}">
  <div class="flex items-start justify-between gap-2">
    <span class="text-[10.5px] font-semibold uppercase tracking-[.06em] text-dark-gray">{card.type}</span>
    <span class="shrink-0 text-[10.5px] text-dark-gray">{card.scope.label}</span>
  </div>
  <h3 class="mt-1.5 font-heading text-[14.5px] font-bold leading-snug text-navy">{card.title}</h3>
  <p class="mt-1 text-[12.5px] leading-relaxed text-dark-gray">{card.summary}</p>

  {#if hasChart}
    <div class="mt-3.5">
      {#if card.render.chart_type === 'sparkline'}
        <Sparkline
          values={data}
          {labels}
          {accent}
          ariaLabel={series?.label ?? card.title}
          unit={series?.unit === 'percent' ? '%' : ''}
        />
      {:else if card.render.chart_type === 'bar'}
        <ColumnChart
          {labels}
          values={data}
          {accent}
          ariaLabel={series?.label ?? card.title}
          unit={series?.unit === 'hours' ? 'h' : ''}
        />
      {:else if card.render.chart_type === 'histogram'}
        <Histogram {labels} values={data} {accent} ariaLabel={series?.label ?? card.title} />
      {:else if card.render.chart_type === 'time_window_band'}
        <TimeBand values={data} {labels} {accent} ariaLabel={series?.label ?? card.title} />
      {/if}
    </div>
  {/if}

  {#if shownMetrics.length > 0}
    <dl class="mt-3.5 flex flex-wrap gap-x-5 gap-y-2 border-t border-toyama-cloudy-gray/60 pt-3">
      {#each shownMetrics as m (m.label)}
        <div>
          <dt class="text-[10.5px] uppercase tracking-[.05em] text-dark-gray">{m.label}</dt>
          <dd class="tym-stat-value text-[15px]">{m.value}</dd>
        </div>
      {/each}
    </dl>
  {/if}
</article>
